import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {useWalkCard} from './useWalkCard';

import type {Category, Walk} from 'types/payload-types';

/**
 * Props for the `WalkCard` component.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'WalkCard'
     */
    testId?: string;
    /** The Walk document to render. */
    walk: Walk;
}

/**
 * Renders a single walk as a card with cover image, title, city, categories,
 * description and metadata (duration, distance, price).
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'WalkCard'`.
 * @param props.walk   The Walk document to display.
 * @returns A JSX element rendering the walk card.
 *
 * @example
 * ```tsx
 * <WalkCard walk={walk} />
 * ```
 */
const WalkCard = ({testId = 'WalkCard', walk}: ComponentProps) => {
    const {
        cityName, href, imgData, isFree, promoPrice, t
    } = useWalkCard(walk);

    return (
        <Wrapper data-cy={testId}>
            <Link href={href}>
                {imgData && (
                    <CardImageWrapper>
                        <ResponsiveImage
                            alt={imgData.alt}
                            focalX={imgData.focalX}
                            focalY={imgData.focalY}
                            sizes="(max-width: 600px) 100vw, 50vw"
                            src={imgData.src}
                            srcSet={imgData.srcSet}
                        />
                    </CardImageWrapper>
                )}
                <CardContent>
                    <CardTitle>{walk.title}</CardTitle>
                    <CardCity>{cityName}</CardCity>
                    {walk.categories && walk.categories.length > 0 && (
                        <CardCategories>
                            {(walk.categories as Category[]).map(cat => {
                                if (typeof cat !== 'object') return null;

                                return (
                                    <CategoryTag key={cat.id}>
                                        {cat.name}
                                    </CategoryTag>
                                );
                            })}
                        </CardCategories>
                    )}
                    <CardDescription>{walk.shortDescription}</CardDescription>
                    <CardMeta>
                        {walk.general?.duration && (
                            <span>
                                {walk.general.duration} {t('common.minutes')}
                            </span>
                        )}
                        {walk.general?.distance && (
                            <span>
                                {walk.general.distance} {t('common.km')}
                            </span>
                        )}
                        {isFree && <span>{t('common.free')}</span>}
                        {!isFree && !promoPrice.hasDiscount && <span>{promoPrice.finalLabel}</span>}
                        {!isFree && promoPrice.hasDiscount && (
                            <span data-cy={`${testId}-promo-price`}>
                                <StrikePrice>{promoPrice.originalLabel}</StrikePrice>{' '}
                                <PromoPrice>{promoPrice.finalLabel}</PromoPrice>
                            </span>
                        )}
                    </CardMeta>
                </CardContent>
            </Link>
        </Wrapper>
    );
};

WalkCard.displayName = 'WalkCard';

export {WalkCard};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    border: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 12px;
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    }

    a {
        color: inherit;
        text-decoration: none;
    }
`;

const CardImageWrapper = styled.div`
    height: 200px;
    overflow: hidden;
    width: 100%;
`;

const CardContent = styled.div`
    padding: ${spacing(4)};
`;

const CardTitle = styled.h2`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2rem;
    margin: 0 0 ${spacing(1)};
`;

const CardCity = styled.span`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
`;

const CardCategories = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(2)};
    margin-top: ${spacing(2)};
`;

const CategoryTag = styled.span`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: 9999px;
    color: ${({theme}) => theme.colors.primaryDefault};
    display: inline-flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.1rem;
    font-weight: 600;
    gap: ${spacing(1)};
    padding: ${spacing(1)} ${spacing(3)};
`;

const CardDescription = styled.p`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    line-height: 1.5;
    margin: ${spacing(2)} 0;
    overflow: hidden;
`;

const CardMeta = styled.div`
    color: ${({theme}) => theme.colors.inkTertiary};
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    gap: ${spacing(3)};
    margin-top: ${spacing(2)};
`;

const StrikePrice = styled.s`
    opacity: 0.7;
`;

const PromoPrice = styled.strong`
    color: ${({theme}) => theme.colors.primaryDefault};
`;